// Bookings list — backed by /admin/bookings.
//
// Filterable + searchable table. Filter chips map to the Hono endpoint's
// status enum (draft/pending/confirmed/cancelled/completed) plus an "all"
// chip. Search hits reference / customer email / car slug server-side.

import { TRANSFERS_UI_ENABLED } from '@/lib/features';
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  DataTable,
  type DataTableColumn,
  type RowKey,
} from '@drivecars/admin-ui';
import {
  Avatar,
  PageHeader,
  ToolBtn,
  ToolChip,
  ToolSearch,
  Toolbar,
} from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  bulkBookingAction,
  cancelBooking,
  completeBooking,
  confirmBooking,
  downloadBookingsCsv,
  fetchAdminBookings,
  type AdminBookingRow,
  type AdminBookingsListParams,
  type BookingBulkAction,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

type FilterKey = NonNullable<AdminBookingsListParams['status']>;
type MethodFilterKey = NonNullable<AdminBookingsListParams['paymentMethod']>;

// Two top-level views so drafts (never paid) + cancelled don't clutter the
// real operating list. Each view's "All" maps to a server-side grouping
// ('active' = pending+confirmed+completed, 'archived' = draft+cancelled).
type ViewKey = 'active' | 'archived';

/**
 * The outermost split: what was sold.
 *
 * A rental and a transfer are different businesses. A rental is bid on and the
 * car handed over; a transfer is assigned to a crew that sells transfers at
 * that airport and then driven by a person. They share a table and almost
 * nothing else — the actions, the eligible crews and the fulfilment all differ
 * — so ops works them as separate queues instead of one mixed list where the
 * wrong action is one row away.
 */
type ProductKey = 'airport-transfers' | 'car-rentals';

// The transfers tab only exists while the product is on (lib/features.ts).
// Rows are not filtered by the flag: a transfer booked before the switch is
// still real money and still needs to be found — it shows up in search.
const PRODUCTS: Array<{ key: ProductKey; label: string }> = [
  ...(TRANSFERS_UI_ENABLED ? [{ key: 'airport-transfers' as const, label: 'Transfers' }] : []),
  { key: 'car-rentals', label: 'Rentals' },
];

const VIEWS: Array<{ key: ViewKey; label: string }> = [
  { key: 'active', label: 'Active' },
  { key: 'archived', label: 'Drafts & cancelled' },
];

const FILTERS_BY_VIEW: Record<ViewKey, Array<{ key: FilterKey; label: string }>> = {
  active: [
    { key: 'active', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'completed', label: 'Completed' },
  ],
  archived: [
    { key: 'archived', label: 'All' },
    { key: 'draft', label: 'Draft' },
    { key: 'cancelled', label: 'Cancelled' },
  ],
};

const METHOD_FILTERS: Array<{ key: MethodFilterKey; label: string }> = [
  { key: 'all', label: 'All methods' },
  { key: 'card', label: 'Card' },
  { key: 'noncard', label: 'Klarna / Crypto' },
];

const fmtMoney = (v: number, currency = 'EUR') =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(v);

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString();

interface ActionCtx {
  busy: number | null;
  onConfirm: (r: AdminBookingRow) => void;
  onCancel: (r: AdminBookingRow) => void;
  onComplete: (r: AdminBookingRow) => void;
}

const buildColumns = (ctx: ActionCtx): DataTableColumn<AdminBookingRow>[] => [
  {
    key: 'reference',
    h: 'Code',
    mono: true,
    cell: (r) => <span>{r.reference}</span>,
  },
  {
    key: 'guest',
    h: 'Guest',
    cell: (r) => (
      <div className="flex items-center gap-2">
        <Avatar name={r.customerName} size={26} />
        <div className="min-w-0">
          <div className="truncate font-medium">{r.customerName}</div>
          <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
            {r.customerEmail ?? '—'}
          </div>
        </div>
      </div>
    ),
  },
  {
    key: 'car',
    h: 'Car',
    cell: (r) => r.carTitle ?? r.carSlug ?? '—',
  },
  {
    key: 'vendor',
    h: 'Vendor',
    cell: (r) => r.vendorName ?? '—',
  },
  {
    key: 'pickup',
    h: 'Pickup → Return',
    cell: (r) => (
      <div className="text-[12px] leading-tight">
        <div>{fmtDate(r.dateFrom)}</div>
        <div className="text-[color:var(--bo-mute)]">{fmtDate(r.dateTo)}</div>
      </div>
    ),
  },
  {
    key: 'units',
    h: 'Units',
    right: true,
    cell: (r) => <span className="tabular-nums">{r.units}</span>,
  },
  {
    key: 'total',
    h: 'Total',
    right: true,
    cell: (r) => <span className="tabular-nums">{fmtMoney(r.total, r.currency)}</span>,
  },
  // What the booking is worth to us and to the partner — in the currency the
  // customer was charged (`moneyCurrency`), which is not always the ledger
  // `currency` beside it. An estimate until the bid settles; the detail page's
  // Money block says so per booking.
  {
    key: 'drivecarsNet',
    h: 'DriveCars',
    right: true,
    cell: (r) => (
      <span className="tabular-nums">
        {r.drivecarsNet != null ? fmtMoney(r.drivecarsNet, r.moneyCurrency ?? r.currency) : '—'}
      </span>
    ),
  },
  {
    key: 'partnerCommission',
    h: 'Partner',
    right: true,
    cell: (r) => (
      <span className="tabular-nums">
        {r.partnerCommission != null
          ? fmtMoney(r.partnerCommission, r.moneyCurrency ?? r.currency)
          : '—'}
      </span>
    ),
  },
  {
    key: 'paymentStatus',
    h: 'Payment',
    cell: (r) => <span className="text-[12px]">{r.paymentStatus ?? 'unpaid'}</span>,
  },
  {
    key: 'status',
    h: 'Status',
    cell: (r) => <StatusPill status={r.status} />,
  },
  {
    key: 'actions',
    h: 'Actions',
    cell: (r) => {
      const busy = ctx.busy === r.id;
      if (r.status === 'pending') {
        return (
          <div className="flex items-center gap-1.5">
            <RowAction kind="primary" busy={busy} onClick={() => ctx.onConfirm(r)}>
              Confirm
            </RowAction>
            <RowAction kind="danger" busy={busy} onClick={() => ctx.onCancel(r)}>
              Cancel
            </RowAction>
          </div>
        );
      }
      if (r.status === 'confirmed') {
        return (
          <div className="flex items-center gap-1.5">
            <RowAction busy={busy} onClick={() => ctx.onComplete(r)}>
              Mark complete
            </RowAction>
            <RowAction kind="danger" busy={busy} onClick={() => ctx.onCancel(r)}>
              Cancel
            </RowAction>
          </div>
        );
      }
      return <span className="text-[11px] text-[color:var(--bo-mute)]">—</span>;
    },
  },
];

export function Bookings() {
  // Transfers first: they are time-critical (someone is landing) in a way a
  // rental booked for next month is not.
  const [product, setProduct] = useState<ProductKey>(TRANSFERS_UI_ENABLED ? 'airport-transfers' : 'car-rentals');
  const [view, setView] = useState<ViewKey>('active');
  const [filter, setFilter] = useState<FilterKey>('active');
  const [methodFilter, setMethodFilter] = useState<MethodFilterKey>('all');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [filter, methodFilter, q, setPage]);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<RowKey>>(new Set());
  const [bulkResult, setBulkResult] = useState<{
    succeeded: number;
    failed: number;
  } | null>(null);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadBookingsCsv({
        status: filter,
        paymentMethod: methodFilter,
        q: q.trim() || undefined,
      });
    } catch (err) {
      setExportError((err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'bookings', { product, filter, methodFilter, q, page }],
    queryFn: ({ signal }) =>
      fetchAdminBookings(
        {
          product,
          status: filter,
          paymentMethod: methodFilter,
          q: q.trim() || undefined,
          page,
          limit,
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
  };

  const confirmMut = useMutation({ mutationFn: confirmBooking, onSuccess: invalidate });
  const cancelMut = useMutation({
    mutationFn: ({ ref, reason }: { ref: string; reason?: string }) => cancelBooking(ref, reason),
    onSuccess: invalidate,
  });
  const completeMut = useMutation({ mutationFn: completeBooking, onSuccess: invalidate });
  const bulkMut = useMutation({
    mutationFn: ({
      ids,
      action,
      reason,
    }: {
      ids: number[];
      action: BookingBulkAction;
      reason?: string;
    }) => bulkBookingAction(ids, action, reason),
    onSuccess: (res) => {
      setBulkResult({ succeeded: res.succeeded.length, failed: res.failed.length });
      setSelectedIds(new Set());
      invalidate();
    },
  });

  const runBulk = (action: BookingBulkAction) => {
    const ids = Array.from(selectedIds, Number).filter(Number.isFinite);
    if (ids.length === 0) return;
    const verb =
      action === 'confirm' ? 'Confirm' : action === 'cancel' ? 'Cancel' : 'Complete';
    if (!window.confirm(`${verb} ${ids.length} booking${ids.length === 1 ? '' : 's'}?`)) {
      return;
    }
    let reason: string | undefined;
    if (action === 'cancel') {
      reason = window.prompt('Cancellation reason (optional):') ?? undefined;
    }
    bulkMut.mutate({ ids, action, reason });
  };

  const busy =
    confirmMut.isPending || cancelMut.isPending || completeMut.isPending
      ? ((confirmMut.variables as string) ??
          (cancelMut.variables as { ref: string } | undefined)?.ref ??
          (completeMut.variables as string) ??
          null)
      : null;

  const cols = buildColumns({
    busy: busy
      ? ((data?.rows ?? []).find((r) => r.reference === busy)?.id ?? null)
      : null,
    onConfirm: (r) => confirmMut.mutate(r.reference),
    onCancel: (r) => {
      const reason = window.prompt('Cancellation reason (optional):') ?? undefined;
      cancelMut.mutate({ ref: r.reference, reason });
    },
    onComplete: (r) => completeMut.mutate(r.reference),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const mutationError =
    confirmMut.error || cancelMut.error || completeMut.error;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Operate"
        title="Bookings"
        sub={`${total.toLocaleString()} ${
          view === 'archived' ? 'draft/cancelled' : 'active'
        } booking(s)${filter !== view ? ` · ${filter}` : ''}${
          methodFilter === 'all'
            ? ''
            : ` · ${methodFilter === 'noncard' ? 'klarna/crypto' : 'card'}`
        }.`}
        actions={
          <>
            <ToolBtn icon="download" onClick={() => void handleExport()}>
              {exporting ? 'Exporting…' : 'Export CSV'}
            </ToolBtn>
            <ToolBtn icon="plus" primary disabled title="Coming soon">
              New booking
            </ToolBtn>
          </>
        }
      />
      {exportError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Export failed: {exportError}
        </div>
      ) : null}
      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn't load bookings: {(error as Error).message}
        </div>
      ) : null}
      {mutationError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Mutation failed: {(mutationError as Error).message}
        </div>
      ) : null}
      {/* The product split sits above everything else, because it changes what
          the rows even mean — and which actions on them are legal. */}
      <Toolbar>
        {PRODUCTS.map((p) => (
          <ToolChip
            key={p.key}
            active={product === p.key}
            onClick={() => {
              setProduct(p.key);
              setPage(1);
            }}
          >
            {p.label}
          </ToolChip>
        ))}
      </Toolbar>
      <Toolbar>
        {VIEWS.map((v) => (
          <ToolChip
            key={v.key}
            active={view === v.key}
            onClick={() => {
              // Switching view resets to that view's "All" grouping.
              setView(v.key);
              setFilter(v.key);
            }}
          >
            {v.label}
          </ToolChip>
        ))}
      </Toolbar>
      <Toolbar>
        <ToolSearch value={q} onChange={setQ} placeholder="Search reference, email, car slug…" />
        {FILTERS_BY_VIEW[view].map((f) => (
          <ToolChip
            key={f.key}
            active={filter === f.key}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </ToolChip>
        ))}
      </Toolbar>
      <Toolbar>
        {METHOD_FILTERS.map((f) => (
          <ToolChip
            key={f.key}
            active={methodFilter === f.key}
            onClick={() => setMethodFilter(f.key)}
          >
            {f.label}
          </ToolChip>
        ))}
      </Toolbar>
      {bulkResult ? (
        <div
          role="status"
          className={`flex items-center justify-between rounded-lg border px-3 py-2 text-[12.5px] ${
            bulkResult.failed === 0
              ? 'border-[#a7f3d0] bg-[#ecfdf5] text-[#065f46]'
              : 'border-[#fde68a] bg-[#fef9c3] text-[#854d0e]'
          }`}
        >
          <span>
            Bulk action complete: {bulkResult.succeeded} succeeded
            {bulkResult.failed > 0 ? `, ${bulkResult.failed} failed` : ''}.
          </span>
          <button
            type="button"
            onClick={() => setBulkResult(null)}
            className="text-[11px] underline-offset-2 hover:underline"
          >
            dismiss
          </button>
        </div>
      ) : null}
      {selectedIds.size > 0 ? (
        <div className="sticky top-[60px] z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 py-2 shadow-sm">
          <div className="text-[12.5px]">
            <strong>{selectedIds.size}</strong> selected
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="ms-3 text-[11.5px] text-[color:var(--bo-mute)] hover:underline"
            >
              clear
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <RowAction
              kind="primary"
              busy={bulkMut.isPending}
              onClick={() => runBulk('confirm')}
            >
              Confirm selected
            </RowAction>
            <RowAction
              busy={bulkMut.isPending}
              onClick={() => runBulk('complete')}
            >
              Mark complete
            </RowAction>
            <RowAction
              kind="danger"
              busy={bulkMut.isPending}
              onClick={() => runBulk('cancel')}
            >
              Cancel selected
            </RowAction>
          </div>
        </div>
      ) : null}
      <DataTable
        columns={cols}
        rows={rows}
        onRowClick={(r) => void navigate({ to: `/bookings/${encodeURIComponent(r.reference)}` as never })}
        emptyLabel={isLoading ? 'Loading…' : 'No matching bookings.'}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
        selection={{
          selectedIds,
          onToggle: (id) => {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id);
              else next.add(id);
              return next;
            });
          },
          onToggleAll: (ids) => {
            setSelectedIds((prev) => {
              const allHere = ids.every((id) => prev.has(id));
              const next = new Set(prev);
              if (allHere) {
                for (const id of ids) next.delete(id);
              } else {
                for (const id of ids) next.add(id);
              }
              return next;
            });
          },
        }}
      />
    </div>
  );
}
